import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { TcsmAppConfigs } from '../../../../global/models/configuration-models';
import { useParticipantSettings } from './hooks/useParticipantSettings';
import { ParticipantsEnrollmentRoutes } from './models/routes';
import { withParticipantSelect } from '../../../../global/components/HOCs/with-participant-select';
import { NewParticipantStepper } from './steppers/new-participant-stepper';
import { NewHireEnrollmentRoutes } from '../new-hire-enrollment/models/new-hire-routes';
import { unSetEmploymentState } from '../employment/employment-slice';
import { unloadCompensationState } from '../compensation/compensation-history/compensation-history-slice';
import { ClearTcsmSessionStorage } from '../../../../global/helpers/session-storage-helper';
import { unloadPersonState } from '../../../account-info/features/personal-info/personal-info-slice';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { unloadRelationshipState } from '../../../account-info/features/relationships/relationships-slice';
import { unloadDependentState } from '../../../account-info/features/dependents/dependents-slice';
import { clearPensionInfo } from '../pension-enrollment/pension-enrollment-slice';
import { clearNewHireEnrollmentState } from '../new-hire-enrollment/slice/new-hire-enrollment-slices';
import { NewHireEnrollmentConfirmation } from '../new-hire-enrollment/confirmation/new-hire-confirmation';
import { withBillingAccountSelect } from '../../../../global/components/HOCs/with-billing-account-select';
import { useBillingAccountNumber } from './hooks/useGroupId';
import { useParticipantWhoWillEnrollContentModel } from './hooks/useParticipantWhoWillEnrollContentModel';
import { TcsmStates } from '../../../../root/tcsm-model';
import { SearchProps } from '../../models/arc-epi-props';
import { BillingAccount } from '../billing-accounts/billing-accounts-models';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';

const WhoWillEnroll = React.lazy(() => import('../new-hire-enrollment/who-will-enroll/who-will-enroll'));
const BenefitEnrollment = React.lazy(() => import('./benefit-enrollment/benefit-enrollment'));
const NewParticipantRecord = React.lazy(() => import('./new-participant/new-participant-record'));
const ExistingParticipantRecord = React.lazy(() => import('./new-participant/existing-participant-record'));
const BenefitEnrollmentWrapper = withParticipantSelect()(BenefitEnrollment);
const WhoWillEnrollWrapper = withParticipantSelect()(WhoWillEnroll);
const NewParticipantWrapper = withBillingAccountSelect()(NewParticipantRecord);
const ExistingParticipantWrapper = withBillingAccountSelect()(ExistingParticipantRecord);

interface Props {
	appSettings: TcsmAppConfigs;
}

function ParticipantEnrollmentApp(props: Props) {

	const { appSettings } = props;
	const settingsLoaded = useParticipantSettings(appSettings?.participantEnrollmentSettingsId, appSettings?.annualEnrollmentSettingsId, appSettings?.newHireSettingsId);
	const routeMatch = useRouteMatch();
	const dispatch = useDispatch();
	const location = useLocation();
	const selectedBillingAccount = useSelector<TcsmStates, BillingAccount>(state => state.billingAccountsState?.currentBillingAccount);
	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
	const allowedAddParticipantGroups = searchSettings?.groupsWhoCanAddParticipants?.split(',');
	const mapHome = useBasePath(ArcRoutes.ArcRoot);
	useEffect(() => {
		return function cleanUp() {

			dispatch(unSetEmploymentState());
			dispatch(unloadCompensationState());
			dispatch(unloadPersonState(DemographicAppKeys.arc));
			dispatch(unloadRelationshipState(DemographicAppKeys.arc));
			dispatch(unloadDependentState(DemographicAppKeys.arc));
			dispatch(clearNewHireEnrollmentState());
			dispatch(clearPensionInfo());
			ClearTcsmSessionStorage(false);
		};
	}, []);

	if (!settingsLoaded) {
		return <LoadingCircle />;
	}

	if (!allowedAddParticipantGroups?.some(gid => gid === selectedBillingAccount?.group_id.toString())) {
		return <Redirect to={mapHome} />;
	}


	return (
		<>
			<NewParticipantStepper basePath={routeMatch.path} hideAll={location?.pathname?.includes(NewHireEnrollmentRoutes.Confirmation)} />
			<Switch>
				<Route path={`${routeMatch.path}${ParticipantsEnrollmentRoutes.ParticipantRecord}`} render={() => <NewParticipantWrapper />} />
				<Route path={`${routeMatch.path}${ParticipantsEnrollmentRoutes.ExistingParticipantRecord}`} render={() => <ExistingParticipantWrapper />} />
				<Route path={`${routeMatch.path}${ParticipantsEnrollmentRoutes.WhoWillEnroll}`} render=
					{
						() =>
							<WhoWillEnrollWrapper
								basePath={routeMatch.path}
								contentModelHook={useParticipantWhoWillEnrollContentModel}
								clientIdHook={useBillingAccountNumber}
								participantEnrollment
							/>
					}
				/>
				<Route path={`${routeMatch.path}${ParticipantsEnrollmentRoutes.BenefitEnrollment}`} render={() => <BenefitEnrollmentWrapper settings={appSettings} />} />
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Confirmation}`} render={() => <NewHireEnrollmentConfirmation settignsId={appSettings?.newHireSettingsId} hideBreadCrumbs/>} />

			</Switch>
		</>
	);
}

export default ParticipantEnrollmentApp;